import { gql, useQuery } from "@apollo/client";
import { ButtonWithArrow } from "components/base/base";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { useModals } from "ModalProvider";
import moment from "moment";
import { isEmpty } from "pages/tools/utils";
import { Fragment, useState } from "react";
import { formatDate } from "react-day-picker/moment";
import {
  BsArrowRight,
  BsFillCalendar3WeekFill,
  BsInfoCircle,
} from "react-icons/bs";
import { IoIosCube } from "react-icons/io";
import { Link } from "react-router-dom";
import ExtraComputeString from "./ExtraComputeString";
import ProductionDatesView from "./ProductionDatesView";
import ProductionsView2025 from "./productionViews/2025";

const FETCH_PLAN_DATA = gql`
  query FETCH_PLAN_DATA($id: ID!) {
    productionPlan(id: $id) {
      id
      data
    }
  }
`;

const ProductionsView = ({ planId }) => {
  const { loading, error, data } = useQuery(FETCH_PLAN_DATA, {
    variables: { id: planId },
    fetchPolicy: "network-only",
  });

  const modal = useModals();

  function showProductionDates(production) {
    modal.present({
      title: "Production Dates",
      maxWidth: "max-w-3xl",
      children: <ProductionDatesView production={production} />,
    });
  }

  if (loading)
    return (
      <div className="p-20">
        <InlineSpinner />
      </div>
    );
  if (error) return <Errors error={error} />;

  const { version, productions } = JSON.parse(data.productionPlan.data);

  if (version === "2025-05")
    return (
      <ProductionsView2025
        productions={productions}
        showProductionDates={showProductionDates}
      />
    );

  const validProductions = productions.filter((i) => i.items.length > 0);

  return (
    <div>
      <h4>Inventory, Purchase and Manufacturing</h4>

      <div className="space-y-4 mt-4 -mx-2">
        {validProductions.length > 0 ? (
          <table>
            <thead>
              <tr className="whitespace-nowrap">
                <th className="py-3 text-left">Production</th>
                <th className="py-3 text-right">Request QTY</th>
                <th className="py-3 text-right">Using Stock(Forecasted)</th>
                <th className="py-3 text-right">To Consumed</th>
                <th className="py-3 text-right">To Produce</th>
              </tr>
            </thead>
            <tbody>
              {validProductions.map((production, index) => (
                <ProductionSection
                  key={index}
                  index={index}
                  production={production}
                  showProductionDates={showProductionDates}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-2">
            No purchase or manufacturing. Make sure each product in orders has
            correct bom and production nodes.
          </div>
        )}
      </div>
    </div>
  );
};

export const ProductionSection = ({
  index,
  production,
  showProductionDates,
}) => {
  const [showPrevPos, setShowPrevPos] = useState(false);

  // Support old data format

  return (
    <Fragment>
      <tr>
        <td
          colSpan={7}
          className={`p-2
                    ${production.production_time < 0 ? "bg-pink-200 text-pink-600" : "bg-gray-100 dark:bg-gray-900 dark:bg-opacity-80"}
                 `}
        >
          <div>
            <div className="flex items-center space-x-3">
              <div className="font-semibold">
                {index + 1}. {production.node.category.name} -{" "}
                {production.node.category.supplier.name}
              </div>
              {production.node.category.calendar && (
                <Link
                  className="flex-shrink-0 whitespace-nowrap"
                  to={`/supplier-calendar/?id=${
                    production.node.category.calendar.id
                  }`}
                  target="_blank"
                >
                  <BsFillCalendar3WeekFill />
                </Link>
              )}
              <span>
                {production.node.category.time_string
                  ? `[
                       ${production.node.category.time_string}]`
                  : null}{" "}
                &times; {production.to_produce}
                {production.production_time < 0
                  ? " - Warning: Qty overflow!!"
                  : null}
              </span>
            </div>

            {production.node.category.extra_computings &&
            production.node.category.extra_computings.length > 0 ? (
              <div>
                <label className="pr-2">Extra Computings:</label>
                {production.node.category.extra_computings.map((i) => (
                  <span key={i.id} className="mr-2">
                    <span>{i.name}: </span>
                    <span>
                      <ExtraComputeString type={i.type} value={i.value} />
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </td>
      </tr>

      {production.items.length === 0 ? (
        <tr>
          <td>No products in this node.</td>
        </tr>
      ) : (
        production.items.map((item, itemIndex) => (
          <tr
            key={itemIndex}
            className="border-y border-gray-50 dark:border-gray-800"
          >
            <td className="px-2 py-1">
              [{item.wis_product.number}] {item.wis_product.name}
            </td>
            <td className="text-right px-2 py-1">{item.qty}</td>
            <td className="text-right px-2 py-1 whitespace-nowrap">
              {item.delivery ? (
                <div className="flex justify-end space-x-2">
                  {item.delivery.type === "PO" ? (
                    <IoIosCube className="text-purple-600 text-base" />
                  ) : item.delivery.type === "SS" ? (
                    <IoIosCube className="text-orange-600 text-base" />
                  ) : (
                    "-"
                  )}
                  <span>{item.delivery.origin}</span>
                  <span>{moment(item.delivery.date).format("YYYY-MM-DD")}</span>
                  {item.delivery.type == "PO" ? (
                    <span>
                      {item.delivery.odoo_forecast}(
                      {item.delivery.final_odoo_forecast})
                    </span>
                  ) : (
                    <span>
                      {item.delivery.ss_forecast}(
                      {item.delivery.final_ss_forecast})
                    </span>
                  )}
                </div>
              ) : (
                "-"
              )}
            </td>
            <td className="text-right px-2 py-1">
              {item.to_consume > 0 ? item.to_consume : "-"}
            </td>
            <td className="text-right px-2 py-1">{item.to_produce}</td>
          </tr>
        ))
      )}

      <tr>
        <td className="p-2 pb-4" colSpan={7}>
          <div className="flex items-baseline space-x-4">
            <div>
              {production.prev_batch ? (
                <div className="text-green-600 font-semibold">
                  Use Previous {production.prev_batch.name}{" "}
                  {formatDate(production.prev_batch.date * 1000, "YYYY-MM-DD")}
                </div>
              ) : null}

              <div className="flex items-baseline space-x-2">
                <div className="text-teal-600 font-semibold text-base flex items-center space-x-2">
                  <span>
                    {moment(production.production_start_date).format(
                      "YYYY-MM-DD",
                    )}
                  </span>
                  <BsArrowRight />
                  <span>
                    {moment(production.ready_date).format("YYYY-MM-DD")}{" "}
                  </span>
                </div>
              </div>

              {production.to_produce > 0 ? (
                <div className="flex items-center space-x-2">
                  <div className="space-x-2 opacity-50">
                    <span>Production: {production.production_time} days</span>
                    {production.transfer_time > 0 ? (
                      <>
                        <span>+</span>
                        <span>Transfer: {production.transfer_time} days</span>
                      </>
                    ) : null}
                  </div>
                  {!isEmpty(production.production_dates_with_qty) && (
                    <Button onClick={() => showProductionDates(production)}>
                      <BsInfoCircle />
                    </Button>
                  )}
                </div>
              ) : null}
            </div>

            {production.prev_pos ? (
              <ButtonWithArrow
                className="text-sky-600"
                show={showPrevPos}
                onClick={() => setShowPrevPos((prev) => !prev)}
              >
                {showPrevPos ? "Hide" : "Show"} Current Production
              </ButtonWithArrow>
            ) : null}
          </div>
        </td>
      </tr>

      {showPrevPos ? (
        <tr>
          <td colSpan={6}>
            <div className="flex">
              <div className="bg-gray-100 dark:bg-gray-900 dark:bg-opacity-50 rounded-2xl p-4 mb-4">
                <div className="px-2">
                  Total qty in PO not confirmed for this production category by
                  month.
                </div>
                <div className="flex">
                  <div className="mt-2">
                    {production.prev_pos.map((po) => (
                      <div
                        key={po.id}
                        className="text-xs opacity-70 flex items-center justify-between border-b dark:border-gray-700 space-x-8"
                      >
                        <div className="px-2 py-1 ">
                          {po.year} - {po.month}
                        </div>

                        <div className="px-2 py-1">{po.assignedQty}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
};

export default ProductionsView;
